import { registerLocaleData } from '@angular/common';
import localeEs from '@angular/common/locales/es';
import localePtBR from '@angular/common/locales/pt';
import localePT from '@angular/common/locales/pt-PT';
import { InjectionToken } from '@angular/core';
import { TranslocoService } from '@jsverse/transloco';
import { setDefaultOptions } from 'date-fns';
import { enUS, es, pt, ptBR } from 'date-fns/locale';
import { KeycloakService } from 'keycloak-angular';
import { lastValueFrom } from 'rxjs';
import { ApplicationsApi, WorkspaceWithServices } from '../my-account-sdk';
import { KeepsPathLocationStrategy, UserProfileService, WorkspaceService } from '../services';

export const KEEPS_DATE_FORMATS = {
  parse: {
    dateInput: 'P',
  },
  display: {
    dateInput: 'P',
    monthYearLabel: 'MMM yyyy',
    dateA11yLabel: 'PPPP',
    monthYearA11yLabel: 'MMMM yyyy',
  },
};

type KpLocaleData = {
  cldrIdentifier: string;
  dateFnsLocale: any;
  angularLocale?: any;
};

export const keycloakInitializer = async (
  keycloak: KeycloakService,
  environment: any,
  workspaceHash: string,
  applicationsApi: ApplicationsApi,
) => {
  const { keycloakConfig, bearerExcludedUrls } = environment;

  const authenticated = await keycloak.init({
    config: keycloakConfig,
    initOptions: {
      onLoad: 'check-sso',
      silentCheckSsoRedirectUri: window.location.origin + '/assets/silent-check-sso.html',
      pkceMethod: 'S256',
    },
    /* features: [
      withAutoRefreshToken({
        onInactivityTimeout: 'none',
        sessionTimeout: 270000,
      }),
    ], */
    //providers: [AutoRefreshTokenService, UserActivityService],
    bearerPrefix: '',
    bearerExcludedUrls,
  });

  if (!authenticated) {
    await redirectToLogin(workspaceHash, applicationsApi, keycloak);
    return false;
  }

  return true;
};

export async function getWorkspaces(applicationsApi: ApplicationsApi) {
  return lastValueFrom(applicationsApi.getWorkspaces());
}

const workspaceInitializer = async (
  applicationsApi: ApplicationsApi,
  workspaceService: WorkspaceService,
  locationStrategy: KeepsPathLocationStrategy,
) => {
  const urlHash = locationStrategy.getHashFromUrl();
  const currentWorkspace = workspaceService.getCurrentWorkspace();
  const workspaces = await getWorkspaces(applicationsApi);
  workspaceService.storeAvailableWorkspaces(workspaces);

  if (urlHash && urlHash === currentWorkspace?.hash_id) {
    return;
  }

  checkBaseHrefWorkspace(urlHash, workspaces, workspaceService);
};

function checkBaseHrefWorkspace(
  urlHash: string,
  workspaces: WorkspaceWithServices[],
  workspaceService: WorkspaceService,
): void {
  if (workspaces.length === 1) {
    workspaceService.setCurrentWorkspace(workspaces[0]);
    return;
  }

  const workspaceFromUrlHash = workspaces.find((workspace) => workspace.hash_id === urlHash);
  if (workspaceFromUrlHash) {
    workspaceService.setCurrentWorkspace(workspaceFromUrlHash);
    return;
  }

  if (urlHash && !workspaceFromUrlHash) {
    workspaceService.clearCurrentWorkspace();
  }
}

export async function registerRolesInitializer(userProfileService: UserProfileService) {
  return await userProfileService.initializeProfile();
}

/**
 * Sets the application language using the transloco library.
 */
export const localeInitializer = (translateService: TranslocoService, keycloakService: KeycloakService) => {
  const tokenParsed = keycloakService.getKeycloakInstance().idTokenParsed as any;
  const currentLocale = tokenParsed?.locale || 'pt-BR';
  translateService.setDefaultLang(currentLocale);
  translateService.setActiveLang(currentLocale);
};

async function initialize(
  keycloak: KeycloakService,
  applicationsApi: ApplicationsApi,
  workspaceService: WorkspaceService,
  userProfileService: UserProfileService,
  translate: TranslocoService,
  locationStrategy: KeepsPathLocationStrategy,
  environment: any,
): Promise<void> {
  try {
    const loggedIn = await keycloakInitializer(
      keycloak,
      environment,
      locationStrategy.getHashFromUrl(),
      applicationsApi,
    );

    if (!loggedIn) {
      return;
    }

    await workspaceInitializer(applicationsApi, workspaceService, locationStrategy);
    localeInitializer(translate, keycloak);

    await registerRolesInitializer(userProfileService);
  } catch (error) {
    console.error(error);
    throw error;
  }
}

export const INIT_ENVIRONMENT = new InjectionToken('app.init.environment');

export function initializer(
  keycloak: KeycloakService,
  applicationsApi: ApplicationsApi,
  workspaceService: WorkspaceService,
  userProfileService: UserProfileService,
  translate: TranslocoService,
  locationStrategy: KeepsPathLocationStrategy,
  environment: any,
): () => Promise<void> {
  return (): Promise<void> => {
    return initialize(
      keycloak,
      applicationsApi,
      workspaceService,
      userProfileService,
      translate,
      locationStrategy,
      environment,
    );
  };
}

export async function redirectToLogin(
  workspaceHash: string,
  applicationsApi: ApplicationsApi,
  keycloak: KeycloakService,
) {
  let loginUrl = await getLoginUrl(workspaceHash, applicationsApi);

  if (!loginUrl) {
    loginUrl = keycloak.getKeycloakInstance().createLoginUrl();
  }

  const redirectURL = new URL(loginUrl);
  redirectURL.searchParams.set('redirect_uri', window.location.href);
  window.location.replace(redirectURL.toString());
}

async function getLoginUrl(workspaceHash: string, applicationsApi: ApplicationsApi): Promise<string | undefined> {
  if (!workspaceHash) {
    return undefined;
  }

  try {
    return await lastValueFrom(applicationsApi.getLoginUrl(workspaceHash));
  } catch (e) {
    return undefined;
  }
}

export function getDateAdapterLocale(keycloakService: KeycloakService) {
  const { locale } = keycloakService.getKeycloakInstance().idTokenParsed;
  return getLocaleInfo(locale).dateFnsLocale;
}

/**
 * Get the current locale to use with angular pipes
 *
 * @param keycloakService
 */
export function initializeAngularLocale(keycloakService: KeycloakService): string {
  const { locale } = keycloakService.getKeycloakInstance().idTokenParsed;
  const localeInfo: KpLocaleData = getLocaleInfo(locale);

  if (localeInfo.angularLocale) {
    registerLocaleData(localeInfo.angularLocale);
  }

  // Sets the date-fns locale globally
  setDefaultOptions({ locale: localeInfo.dateFnsLocale });

  // We need to return the locale string identifier as per the CLDR specification
  return localeInfo.cldrIdentifier;
}

/**
 * Returns the correct locale information
 * @param locale the Keycloak user locale
 */
function getLocaleInfo(locale: string) {
  const localeMaps = new Map<string, KpLocaleData>([
    ['pt-BR', { cldrIdentifier: 'pt-BR', angularLocale: localePtBR, dateFnsLocale: ptBR }],
    ['es', { cldrIdentifier: 'en-ES', angularLocale: localeEs, dateFnsLocale: es }],
    ['pt-PT', { cldrIdentifier: 'pt-PT', angularLocale: localePT, dateFnsLocale: pt }],
    ['en', { cldrIdentifier: 'en-US', dateFnsLocale: enUS }],
  ]);

  return localeMaps.get(locale) || localeMaps.get('pt-BR');
}
